import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { Company, MutatorTx, Schema } from "../schema"
import type {
	CreateValue,
	DeleteCompaniesArgs,
	SetCompaniesSubscribedArgs,
} from "../types"
import type { DeleteID, UpdateValue } from "@rocicorp/zero"

export const companyMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateValue<Company>
			ctx: ZeroJwtPayload
		}) => {
			const [existingCompany] = await tx.query.companies
				.where("domain", "=", args.domain.toLowerCase())
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (existingCompany?.linearCustomerId) {
				throw new Error("Company already exists")
			}

			await tx.mutate.companies.insert({
				domain: args.domain.toLowerCase(),
				domains: args.domains,
				name: args.name,
				id: args.id,
				linearCustomerId: args.linearCustomerId ?? null,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["companies"]>
		}) => {
			// TODO: not sure what all this spreading is about?
			// Seems like tx.mutate.companies.update(args) would work just fine
			await tx.mutate.companies.update({
				id: args.id,
				name: args.name,
				domains: args.domains,
				autoAdd: args.autoAdd,
				...(args.name ? { name: args.name } : {}),
				...(args.size || args.size === 0 ? { size: args.size } : {}),
				...(args.revenue || args.revenue === 0
					? { revenue: args.revenue }
					: {}),
				...(args.tierId ? { tierId: args.tierId } : {}),
				...(args.tierName ? { tierName: args.tierName } : {}),
				...("linearOwnerId" in args
					? { linearOwnerId: args.linearOwnerId }
					: {}),
				...("linearOwnerName" in args
					? { linearOwnerName: args.linearOwnerName }
					: {}),
				...("linearOwnerAvatarUrl" in args
					? { linearOwnerAvatarUrl: args.linearOwnerAvatarUrl }
					: {}),
				...(args.statusId ? { statusId: args.statusId } : {}),
				...(args.statusName ? { statusName: args.statusName } : {}),
				...(args.statusColor ? { statusColor: args.statusColor } : {}),
				...(args.linearCustomerId
					? { linearCustomerId: args.linearCustomerId }
					: {}),
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["companies"]>
		}) => {
			await tx.mutate.companies.delete({
				id: args.id,
			})
		},
	),
	deleteMany: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteCompaniesArgs }) => {
			for (const id of args.ids) {
				await tx.mutate.companies.delete({ id })
			}
		},
	),
	// Optimistic for the people this client has synced; the server mutator
	// updates the rest in a few statements per chunk.
	setSubscribedMany: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetCompaniesSubscribedArgs
		}) => {
			if (args.ids.length === 0) return
			const [primary, links] = await Promise.all([
				tx.query.contacts
					.where("companyId", "IN", args.ids)
					.where("isSubscribed", !args.isSubscribed)
					.run(),
				tx.query.contactCompanies.where("companyId", "IN", args.ids).run(),
			])
			const contactIds = new Set([
				...primary.map((contact) => contact.id),
				...links.map((link) => link.contactId),
			])
			for (const id of contactIds) {
				await tx.mutate.contacts.update({
					id,
					isSubscribed: args.isSubscribed,
				})
			}
		},
	),
}
